import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getCommitteeContext, setCommitteeContext } from '@/utils/committeeContext';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DynamicFormRenderer } from '@/components/DynamicFormRenderer';
import { 
  ArrowLeft, ArrowRight, Save, Send, UserPlus, 
  CheckCircle, AlertCircle, Clock, Lock, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_UPLOAD_EXTENSIONS = ['pdf', 'docx', 'xls', 'jpg', 'jpeg', 'png', 'csv'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

const ApplicationForm = () => {
  const { committeeSlug, scholarshipId, applicationId } = useParams();
  const navigate = useNavigate();
  const { user, logout, stopImpersonation } = useAuth();
  const [committeeContext, setCommitteeContextState] = useState(committeeSlug ? getCommitteeContext() : null);
  const [currentStep, setCurrentStep] = useState(0);
  const dashboardPath = committeeSlug ? `/${committeeSlug}/dashboard` : '/dashboard';
  const applicationBasePath = committeeSlug ? `/${committeeSlug}/application` : '/application';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scholarship, setScholarship] = useState(null);
  const [application, setApplication] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [sendingEndorsement, setSendingEndorsement] = useState(false);

  // Dynamic form data - stores all field values by field ID
  const [formData, setFormData] = useState({});
  const [uploadingFieldId, setUploadingFieldId] = useState(null);

  // Check if deadline has passed
  const parseDeadlineForComparison = (deadlineStr) => {
    if (!deadlineStr) return null;
    if (deadlineStr.length === 10) {
      const [year, month, day] = deadlineStr.split('-').map(Number);
      return new Date(year, month - 1, day, 23, 59, 59, 999);
    }
    const parsed = new Date(deadlineStr);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isDeadlinePassed = useCallback(() => {
    if (!scholarship?.deadline) return false;
    if (scholarship?.is_active && ['draft', 'pending_sponsor', 'endorsed'].includes(application?.status)) {
      return false;
    }
    const deadline = parseDeadlineForComparison(scholarship.deadline);
    if (!deadline) return false;
    return new Date() > deadline;
  }, [scholarship, application]);

  const formatReviewValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) return `${value.length} items`;
    if (typeof value === 'object') {
      const fileName = value.name || value.display_name || value.original_name || value.filename;
      if (fileName) return fileName;
      if (value.label) return value.label;
      if (value.value) return value.value;
      return 'Provided';
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  // Get steps from form_config
  const getSteps = useCallback(() => {
    if (!scholarship?.form_config) return [];
    return scholarship.form_config
      .filter(section => section.enabled)
      .map((section, index) => ({
        id: index,
        key: section.id,
        title: section.title,
        description: section.description,
        fields: section.fields || []
      }));
  }, [scholarship]);

  const steps = getSteps();

  const fetchData = useCallback(async () => {
    try {
      let resolvedContext = committeeContext;
      if (committeeSlug && !resolvedContext) {
        const committeeRes = await axios.get(`${API}/public/committees/${committeeSlug}`);
        resolvedContext = committeeRes.data?.committee || null;
        if (resolvedContext) {
          setCommitteeContext(resolvedContext);
          setCommitteeContextState(resolvedContext);
        }
      }

      const scholarshipRes = await axios.get(`${API}/scholarships/${scholarshipId}`);
      const scholarshipData = scholarshipRes.data;
      if (committeeSlug && resolvedContext?.id && scholarshipData?.committee_id && scholarshipData.committee_id !== resolvedContext.id) {
        toast.error('This scholarship does not belong to the selected committee.');
        navigate(dashboardPath);
        return;
      }
      setScholarship(scholarshipData);

      if (applicationId) {
        const appRes = await axios.get(`${API}/applications/${applicationId}`);
        setApplication(appRes.data);
        // Merge existing form data
        setFormData(appRes.data.form_data || {});
      } else {
        // Pre-fill with user info if available
        setFormData(prev => ({
          ...prev,
          first_name: user?.first_name || '',
          family_name: user?.last_name || '',
          email: user?.email || ''
        }));
      }
    } catch (error) {
      toast.error('Failed to load application data');
      navigate(dashboardPath);
    } finally {
      setLoading(false);
    }
  }, [scholarshipId, applicationId, navigate, user, committeeSlug, committeeContext, dashboardPath]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveApplication = async (showToast = true) => {
    // Check deadline before saving
    if (isDeadlinePassed()) {
      toast.error('The deadline has passed. Applications can no longer be modified.');
      return null;
    }
    
    setSaving(true);
    try {
      const data = {
        scholarship_id: scholarshipId,
        form_data: formData,
        // Keep backward compatibility with old structure
        applicant_info: {
          first_name: formData.first_name || '',
          middle_name: formData.middle_name || '',
          family_name: formData.family_name || '',
          email: formData.email || '',
          father_name: formData.father_name || '',
          mother_name: formData.mother_name || '',
          greek_heritage: formData.greek_heritage || '',
          long_term_aspirations: formData.long_term_aspirations || ''
        },
        academic_info: {
          high_school_name: formData.high_school_name || '',
          graduation_date: formData.graduation_date || '',
          current_institution: formData.current_institution || '',
          year_of_study: formData.year_of_study || '',
          program: formData.program || '',
          next_year_institution: formData.next_year_institution || '',
          awards: formData.awards || []
        },
        activities_info: {
          extracurricular: formData.extracurricular || [],
          volunteer: formData.volunteer || [],
          work_experience: formData.work_experience || []
        }
      };

      let response;
      if (applicationId || application?.id) {
        response = await axios.put(`${API}/applications/${applicationId || application.id}`, data);
      } else {
        response = await axios.post(`${API}/applications`, data);
        setApplication(response.data);
        navigate(`${applicationBasePath}/${scholarshipId}/${response.data.id}`, { replace: true });
      }
      
      setLastSaved(new Date());
      if (showToast) toast.success('Progress saved! You can return to complete this application later.');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const ensureApplicationExists = async () => {
    if (application?.id || applicationId) {
      return application?.id || applicationId;
    }
    const created = await saveApplication(false);
    return created?.id;
  };

  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
      toast.error('Unsupported file type. Allowed: pdf, docx, xls, jpg, png, csv');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('File exceeds 5MB size limit');
      return;
    }

    let displayName = file.name;
    if (formData[fieldId]) {
      const rename = window.prompt('A file already exists. Enter a new filename to replace it, or Cancel to stop upload.', file.name);
      if (rename === null) return;
      const trimmed = rename.trim();
      if (trimmed) {
        const hasExtension = trimmed.includes('.');
        displayName = hasExtension ? trimmed : `${trimmed}.${ext}`;
      }
    }

    setUploadingFieldId(fieldId);
    try {
      const appId = await ensureApplicationExists();
      if (!appId) {
        toast.error('Please save your application before uploading files.');
        return;
      }
      const payload = new FormData();
      payload.append('file', file);
      payload.append('field_id', fieldId);
      payload.append('display_name', displayName);

      const response = await axios.post(`${API}/applications/${appId}/files`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedFile = response.data?.file;
      if (uploadedFile) {
        setFormData(prev => ({ ...prev, [fieldId]: uploadedFile }));
        setApplication(prev => prev ? {
          ...prev,
          form_data: { ...(prev.form_data || {}), [fieldId]: uploadedFile },
          reupload_requests: response.data?.reupload_requests || prev.reupload_requests
        } : prev);
        toast.success('File uploaded successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFieldId(null);
    }
  };

  const handleFileRemove = async (fieldId, value) => {
    if (!value) return;
    const fileId = typeof value === 'object' ? value.file_id : null;
    const confirmed = window.confirm('Remove this file from your application?');
    if (!confirmed) return;
    try {
      if (fileId && (application?.id || applicationId)) {
        await axios.delete(`${API}/applications/${applicationId || application.id}/files/${fileId}`);
      }
      setFormData(prev => ({ ...prev, [fieldId]: null }));
      setApplication(prev => prev ? {
        ...prev,
        form_data: { ...(prev.form_data || {}), [fieldId]: null }
      } : prev);
      toast.success('File removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove file');
    }
  };

  const handleExitImpersonation = async () => {
    try {
      const impersonator = await stopImpersonation();
      if (impersonator?.role === 'admin') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/my-committees';
      }
    } catch (error) {
      toast.error('Failed to exit impersonation');
    }
  };


  const saveAndExit = async () => {
    try {
      await saveApplication(true);
      navigate(dashboardPath);
    } catch (error) {
      // Error already handled in saveApplication
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleNext = async () => {
    if (isReadOnly) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
      return;
    }
    await saveApplication(false);
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const inviteSponsor = async () => {
    if (!sponsorEmail || !sponsorName) {
      toast.error('Please fill in endorser details');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/applications/${application?.id || applicationId}/invite-sponsor`, {
        endorser_email: sponsorEmail,
        endorser_name: sponsorName
      });
      toast.success('Endorsement invitation sent! The endorser will receive an email with a link to complete the endorsement.');
      setShowInviteDialog(false);
      setSponsorEmail('');
      setSponsorName('');
      fetchData();
      
      // Log the endorsement link for testing
      if (response.data.endorsement_link) {
        console.log('Endorsement link (for testing):', response.data.endorsement_link);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send endorsement invitation');
    }
  };

  const sendEndorsementRequest = async () => {
    // First save the application to ensure endorser data is stored
    await saveApplication(false);
    
    setSendingEndorsement(true);
    try {
      const response = await axios.post(`${API}/applications/${application?.id || applicationId}/send-endorsement-request`);
      toast.success('Endorsement request sent! The endorser will receive an email with a link to complete the endorsement.');
      fetchData();
      
      // Log the endorsement link for testing
      if (response.data.endorsement_link) {
        console.log('Endorsement link (for testing):', response.data.endorsement_link);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send endorsement request');
    } finally {
      setSendingEndorsement(false);
    }
  };

  const submitApplication = async () => {
    try {
      await axios.post(`${API}/applications/${application?.id || applicationId}/submit`);
      toast.success('Application submitted successfully!');
      navigate(dashboardPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const deadlinePassed = isDeadlinePassed();
  const reuploadRequests = application?.reupload_requests || [];
  const allowImpersonationUploads = user?.is_impersonating && ['admin', 'chair', 'co_chair'].includes(user?.impersonator_role);
  const reuploadRequestMap = reuploadRequests.reduce((acc, req) => {
    if (req?.field_id && req?.status === 'requested') {
      acc[req.field_id] = req.message || 'Committee requested an updated document.';
    }
    return acc;
  }, {});
  const hasActiveReuploadRequests = Object.keys(reuploadRequestMap).length > 0;
  const isReadOnly = deadlinePassed || application?.status === 'submitted' || application?.status === 'under_review' || 
                     application?.status === 'awarded' || application?.status === 'rejected';
  const allowReuploadMode = isReadOnly && (hasActiveReuploadRequests || allowImpersonationUploads);

  const isFieldDisabled = (field) => {
    if (uploadingFieldId === field?.id) return true;
    if (!isReadOnly) return false;
    if (field?.type === 'file' && (reuploadRequestMap[field.id] || allowImpersonationUploads)) return false;
    return true;
  };

  const currentSection = steps[currentStep];
  const isSponsorSection = currentSection?.key === 'sponsor' || currentSection?.key === 'chapter_endorsement';
  const isReviewSection = currentSection?.key === 'review';

  // Format deadline for display
  const formatDeadline = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Deadline Warning Banner */}
      {deadlinePassed && (
        <div className="bg-red-600 text-white py-3 px-6">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <Lock className="w-5 h-5" />
            <span className="font-semibold">APPLICATION CLOSED</span>
            <span className="text-red-100">- The deadline ({formatDeadline(scholarship?.deadline)}) has passed. This application can no longer be modified.</span>
          </div>
        </div>
      )}

      {/* Deadline Reminder Banner (if not passed) */}
      {!deadlinePassed && scholarship?.deadline && (
        <div className="bg-amber-500 text-white py-2 px-6">
          <div className="max-w-4xl mx-auto flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">
              <strong>Deadline:</strong> {formatDeadline(scholarship.deadline)}
            </span>
          </div>
        </div>
      )}

      {/* Impersonation Banner */}
      {user?.is_impersonating && (
        <div className="bg-blue-600 text-white py-2 px-6">
          <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">
                Impersonation Mode: viewing as {user.first_name} {user.last_name}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExitImpersonation}
              className="border-white text-white hover:bg-white/10"
              data-testid="exit-impersonation-btn"
            >
              Exit Impersonation
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={dashboardPath}
                className="text-slate-500 hover:text-greek-blue transition-colors"
                title="Back to Dashboard"
                data-testid="back-to-dashboard-link"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-heading text-lg font-bold text-slate-900">{scholarship?.name}</h1>
                <p className="text-sm text-slate-500">
                  Application Form
                  {lastSaved && (
                    <span className="ml-2 text-emerald-600">
                      (Last saved: {lastSaved.toLocaleTimeString()})
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isReadOnly && (
                <Button
                  onClick={() => saveApplication()}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                  data-testid="save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Progress'}
                </Button>
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                className="rounded-sm border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2 overflow-x-auto">
            <div className="flex items-center w-full min-w-0">
              {steps.map((step, index) => (
                <div key={step.key} className="flex items-center flex-shrink-0" style={{ flex: index < steps.length - 1 ? '1 1 0%' : '0 0 auto' }}>
                  <button
                    onClick={() => (!isReadOnly || allowReuploadMode) && setCurrentStep(index)}
                    className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-semibold transition-colors flex-shrink-0 ${
                      currentStep === index
                        ? 'bg-greek-blue text-white'
                        : currentStep > index
                        ? 'bg-greek-gold text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                    data-testid={`step-${index}-btn`}
                  >
                    {currentStep > index ? <CheckCircle className="w-3 h-3 md:w-4 md:h-4" /> : index + 1}
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 md:mx-2 min-w-[8px] ${currentStep > index ? 'bg-greek-gold' : 'bg-slate-200'}`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <p className="text-center font-medium text-slate-700 text-sm md:text-base">{currentSection?.title || 'Application'}</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {isReadOnly && !deadlinePassed && !allowReuploadMode && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-sm flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <p className="text-blue-700">This application has been submitted and cannot be edited.</p>
          </div>
        )}

        {allowReuploadMode && allowImpersonationUploads && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-sm flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-emerald-600" />
            <p className="text-emerald-700">Impersonation mode: you can upload required documents even though the application is submitted.</p>
          </div>
        )}

        {allowReuploadMode && !allowImpersonationUploads && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <p className="text-amber-700">Your committee requested updated documents. You can upload files for the requested fields below.</p>
          </div>
        )}

        {deadlinePassed && !allowReuploadMode && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-3">
            <Lock className="w-5 h-5 text-red-600" />
            <p className="text-red-700">The application deadline has passed. This application is now locked and cannot be modified.</p>
          </div>
        )}

        {/* Sponsor Section - Special handling */}
        {isSponsorSection && (
          <div className="bg-white rounded-sm border border-slate-200 p-8 space-y-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">
              {currentSection.title}
            </h2>

            {application?.sponsor_endorsement ? (
              <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                  <div>
                    <h3 className="font-semibold text-emerald-800">Application Endorsed!</h3>
                    <p className="text-sm text-emerald-600">
                      Endorsed by {application.sponsor_endorsement.endorser_name}
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Endorser:</span>
                    <span className="font-medium text-slate-800">{application.sponsor_endorsement.endorser_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Affiliation:</span>
                    <span className="font-medium text-slate-800">{application.sponsor_endorsement.affiliation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Chapter:</span>
                    <span className="font-medium text-slate-800">{application.sponsor_endorsement.chapter}</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Render endorser form fields from form config */}
                {currentSection?.fields?.length > 0 && (
                  <div className="space-y-6 mb-6">
                    <p className="text-slate-600">
                      Please provide your endorser's information below. They will receive an email with a link to complete the endorsement.
                    </p>
                    <div className="space-y-6">
                      {currentSection.fields.map((field) => (
                        <div key={field.id} className="space-y-2">
                          <label className="block text-sm font-medium text-slate-700">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {field.type === 'select' ? (
                            <select
                              value={formData[field.id] || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
                              disabled={isReadOnly}
                              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-greek-blue disabled:bg-slate-100"
                            >
                              <option value="">Select {field.label}</option>
                              {field.options?.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.type || 'text'}
                              value={formData[field.id] || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
                              placeholder={field.placeholder}
                              disabled={isReadOnly}
                              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-greek-blue disabled:bg-slate-100"
                            />
                          )}
                          {field.help_text && <p className="text-xs text-slate-500">{field.help_text}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Send Endorsement Request button or status */}
                <div className="p-6 bg-slate-50 rounded-sm">
                  {application?.endorsement_invite ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                      <div className="flex items-center gap-3 mb-3">
                        <AlertCircle className="w-6 h-6 text-amber-600" />
                        <div>
                          <h3 className="font-semibold text-amber-800">Awaiting Endorsement</h3>
                          <p className="text-sm text-amber-600">
                            An endorsement request was sent to {application.endorsement_invite.email}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        Sent on: {new Date(application.endorsement_invite.sent_at).toLocaleDateString()}
                      </p>
                      <Button
                        onClick={sendEndorsementRequest}
                        disabled={sendingEndorsement}
                        variant="outline"
                        className="mt-4 rounded-sm"
                        data-testid="resend-endorsement-btn"
                      >
                        {sendingEndorsement ? (
                          <>Sending...</>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Resend Endorsement Request
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-slate-600 mb-4">
                        Once you've filled in the endorser information above, click the button below to send them the endorsement request.
                      </p>
                      <Button
                        onClick={sendEndorsementRequest}
                        disabled={sendingEndorsement || !formData.endorser_email || !formData.endorser_name}
                        className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
                        data-testid="send-endorsement-btn"
                      >
                        {sendingEndorsement ? (
                          <>Sending...</>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Endorsement Request
                          </>
                        )}
                      </Button>
                      {(!formData.endorser_email || !formData.endorser_name) && (
                        <p className="text-sm text-slate-500 mt-2">
                          Please fill in the endorser name and email above
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Review Section - Special handling */}
        {isReviewSection && (
          <div className="bg-white rounded-sm border border-slate-200 p-8 space-y-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900 border-b border-slate-200 pb-4">
              Review Your Application
            </h2>

            <div className="space-y-6">
              {steps.filter(s => s.key !== 'sponsor' && s.key !== 'review').map((section) => {
                const safeFields = section.fields || [];
                return (
                  <div key={section.key} className="p-4 bg-slate-50 rounded-sm">
                    <h3 className="font-semibold text-slate-800 mb-2">{section.title}</h3>
                    <div className="space-y-1 text-sm text-slate-600">
                      {safeFields.slice(0, 3).map(field => (
                        <p key={field.id}>
                          <span className="font-medium">{field.label}:</span>{' '}
                          {formatReviewValue(formData[field.id])}
                        </p>
                      ))}
                      {safeFields.length > 3 && (
                        <p className="text-slate-400">...and {safeFields.length - 3} more fields</p>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="p-4 bg-slate-50 rounded-sm">
                <h3 className="font-semibold text-slate-800 mb-2">Sponsor Endorsement</h3>
                {application?.sponsor_endorsement ? (
                  <p className="text-emerald-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Endorsed by {application.sponsor_endorsement.co_chair_name}
                  </p>
                ) : (
                  <p className="text-amber-600 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Pending endorsement
                  </p>
                )}
              </div>
            </div>

            {!isReadOnly && application?.sponsor_endorsement && (
              <div className="pt-6 border-t border-slate-200">
                <Button
                  onClick={submitApplication}
                  className="w-full bg-greek-gold hover:bg-greek-gold-dark text-white rounded-sm py-6 text-lg"
                  data-testid="submit-application-btn"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Submit Application
                </Button>
                <p className="text-center text-sm text-slate-500 mt-4">
                  Once submitted, you cannot edit your application.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Dynamic Form Sections */}
        {!isSponsorSection && !isReviewSection && scholarship?.form_config && (
          <DynamicFormRenderer
            formConfig={scholarship.form_config.filter(s => s.id !== 'sponsor' && s.id !== 'review')}
            formData={formData}
            onFormDataChange={setFormData}
            currentSection={currentStep}
            disabled={isReadOnly && !allowReuploadMode}
            onFileUpload={handleFileUpload}
            onFileRemove={handleFileRemove}
            fieldNotes={reuploadRequestMap}
            isFieldDisabled={isFieldDisabled}
          />
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
          <Button
            onClick={handlePrev}
            disabled={currentStep === 0}
            variant="outline"
            className="rounded-sm border-slate-300"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex items-center gap-3">
            {/* Save & Exit Button (always visible when not read-only) */}
            {!isReadOnly && (
              <Button
                onClick={saveAndExit}
                disabled={saving}
                variant="outline"
                className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="save-exit-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save & Exit'}
              </Button>
            )}
            
            {/* Back to Dashboard for read-only mode */}
            {isReadOnly && (
              <Link to={dashboardPath} data-testid="back-to-dashboard-readonly-link">
                <Button
                  variant="outline"
                  className="rounded-sm border-slate-300 text-slate-600 hover:bg-slate-50"
                  data-testid="back-to-dashboard-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
            )}
            
            {currentStep < steps.length - 1 && (
              <Button
                onClick={handleNext}
                disabled={isReadOnly && !allowReuploadMode}
                className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm"
                data-testid="next-step-btn"
              >
                {isReadOnly ? 'Next' : 'Save & Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Invite Endorser Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Request Chapter Endorsement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Enter the details of your AHEPA family member who will endorse your application. 
              They will receive an email with a link to complete the endorsement.
            </p>
            <div className="space-y-2">
              <Label>Endorser's Full Name *</Label>
              <Input
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
                placeholder="Enter endorser's full name"
                className="rounded-sm"
                data-testid="input-endorser-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Endorser's Email Address *</Label>
              <Input
                type="email"
                value={sponsorEmail}
                onChange={(e) => setSponsorEmail(e.target.value)}
                placeholder="endorser@email.com"
                className="rounded-sm"
                data-testid="input-endorser-email"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={inviteSponsor} 
              disabled={!sponsorName.trim() || !sponsorEmail.trim()}
              className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm" 
              data-testid="send-endorsement-btn"
            >
              Send Endorsement Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationForm;
